/**
 * Reusable UI patterns shared across pages.
 *
 * These keep page code declarative and, more importantly, keep accessibility
 * decisions in one place: a page cannot accidentally ship an un-headed section or
 * an error that only shows colour.
 */

import { el, icon } from "./dom.js";

/**
 * A page header: the h1 and an optional plain-language intro.
 *
 * @param {string} title
 * @param {string} [description]
 * @returns {DocumentFragment}
 */
export function pageHeader(title, description) {
  const frag = document.createDocumentFragment();
  frag.append(el("h1", {}, [title]));
  if (description) {
    frag.append(el("p", { class: "kin-lead kin-prose" }, [description]));
  }
  return frag;
}

/**
 * An error alert. Uses a Web Awesome callout with the danger variant AND an
 * explicit icon and heading, so the error is conveyed by more than colour
 * (WCAG 2.2 AA 1.4.1). Errors are written in plain language (§16).
 *
 * @param {string} message
 * @returns {HTMLElement}
 */
export function errorAlert(message) {
  return el("wa-callout", { variant: "danger", role: "alert", class: "kin-alert" }, [
    icon("triangle-exclamation"),
    el("span", {}, [message]),
  ]);
}

/**
 * A success confirmation.
 *
 * @param {string} message
 * @returns {HTMLElement}
 */
export function successAlert(message) {
  return el("wa-callout", { variant: "success", role: "status", class: "kin-alert" }, [
    icon("circle-check"),
    el("span", {}, [message]),
  ]);
}

/**
 * The emergency-services notice.
 *
 * Required on the retrieval journey (CLAUDE.md, §14): KinCode must never present
 * itself as an emergency service, and the retrieval page must direct real
 * emergencies to 999/112. Given its safety importance it renders as a prominent,
 * non-dismissible notice.
 *
 * **Deliberately not a `wa-callout`.** A themed callout takes its colours from the
 * brand palette, and the Look Awesome palette resolves "warning" to a soft green —
 * which does not read as urgent to someone scanning the page in a hurry. This is
 * the one component that must look the same whatever the brand does, so it is
 * plain markup styled by `.kin-emergency` against pinned tokens.
 *
 * @returns {HTMLElement}
 */
export function emergencyNotice() {
  return el("div", { class: "kin-emergency" }, [
    icon("triangle-exclamation", { label: "Important" }),
    el("div", {}, [
      el("strong", {}, ["In an emergency, call 999 (or 112)."]),
      el("p", {}, [
        "KinCode helps you find someone's saved contacts. It is not an emergency " +
          "service and cannot guarantee it will be available when you need it.",
      ]),
    ]),
  ]);
}

/**
 * A labelled section with a heading, so every group of content is reachable in
 * the document outline.
 *
 * @param {string} heading
 * @param {(Node | string)[]} children
 * @returns {HTMLElement}
 */
export function section(heading, children) {
  return el("section", {}, [el("h2", {}, [heading]), ...children]);
}
